import { useEffect, useMemo, useState } from 'react';

import { Builder } from '@builder.io/react';

import { getCategories } from '~/shared/utils/categoryNav';
import { getCategoryNavContainer } from '~/shared/utils/getCategoryNavContainer';
import { useRegion } from '~/techstyle-shared/react-intl';
import { useNav } from '~/techstyle-shared/react-navigation';

import { autoResolveBuilderLocale } from './autoResolveBuilderLocale';
import { getProductGridItemCategoryNavSettings } from './getProductGridItemCategoryNavSettings';
import { useProductGridDefaultParameters } from './useProductGridDefaultParameters';

export function useBuilderCollectionCategoryNav({ options, serverSideData }) {
  const [categoryNavSettings, setCategoryNavSettings] = useState();
  const productGridParameters = useProductGridDefaultParameters();
  const region = useRegion();

  // get the category nav input
  let categoryPath;
  let categoryNavContainer;
  if (process.browser) {
    categoryPath = new URL(window.location);
    categoryNavContainer =
      options.categoryNavContainer ||
      getCategoryNavContainer(region, categoryPath.pathname);
  }
  const categoryNavData = useNav(categoryNavContainer);

  const categoryNav = useMemo(() => {
    if (categoryNavData?.data?.navItems?.length && categoryPath) {
      return getCategories({
        navItems: categoryNavData?.data?.navItems,
        asPath: categoryPath?.pathname,
      });
    }
    return {};
    // we don't include categoryPath so we don't trigger this on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryNavData]);

  useEffect(() => {
    const { sideNav } = Builder.isPreviewing
      ? autoResolveBuilderLocale('Default', options)
      : options;

    // fit into node structure so getProductGridItemCategoryNavSettings can be
    // backwards compatible
    const categoryNavSettingsParameters = {
      categoryNav,
      node: {
        categoryNavContainer: sideNav?.categoryNavContainer,
        customFields: {
          shouldShowCategoryDropdown: !!sideNav?.shouldShowCategoryDropdown,
          shouldShowCategoryHeading: !!sideNav?.shouldShowCategoryHeading,
        },
      },
    };

    const getCategoryNavSettings = async () => {
      // only run this if the server side data was not set
      if (serverSideData) {
        return null;
      }

      const categoryNavSettings = await getProductGridItemCategoryNavSettings(
        categoryNavSettingsParameters
      );
      setCategoryNavSettings(categoryNavSettings);
    };

    getCategoryNavSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, productGridParameters, serverSideData, categoryNav]);

  return categoryNavSettings;
}
